import {authMiddleware, optionalAuthMiddleware} from '@/helpers/authMiddleware';
import {prismaClient} from '@/helpers/prismaClient.server';
import {extractRequestContext, trackEvent} from '@/helpers/mixpanel.server';
import {createServerFn} from '@tanstack/react-start';
import {getRequest} from '@tanstack/react-start/server';
import {z} from 'zod';
import {EventAttendance, MarkAttendingInput} from '@/apiTypes/attendance';
import {
  createReminderSchedule,
  deleteReminderSchedule,
} from '@/helpers/eventBridgeScheduler.server';

/**
 * Marks the authenticated user as attending an external event.
 * Validates that the event exists and has an externalId.
 */
export const markAttending = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof MarkAttendingInput> => {
    const result = MarkAttendingInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const event = await db.event.findUnique({
      where: {id: data.eventId},
      select: {id: true, externalId: true, sourceUrl: true, startDate: true},
    });
    if (!event) throw new Error('Event not found');
    if (!event.sourceUrl) {
      throw new Error('Attendance tracking is only for external events');
    }

    const attendance = await db.eventAttendance.upsert({
      where: {
        profileId_eventId: {
          profileId: profile.id,
          eventId: data.eventId,
        },
      },
      create: {
        profileId: profile.id,
        eventId: data.eventId,
      },
      update: {},
    });

    // Schedule reminder email 24h before event start
    if (event.startDate) {
      const fireAt = new Date(
        event.startDate.getTime() - 24 * 60 * 60 * 1000,
      );
      createReminderSchedule({
        profileId: profile.id,
        eventId: data.eventId,
        type: 'PLANNED_TO_RIDE',
        fireAt,
      }).catch((err) =>
        console.error('Failed to schedule attendance reminder:', err),
      );
    }

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      profile.id,
      'Event Registered',
      {
        event_id: data.eventId,
        intent: 'track',
        category_id: null,
        route_id: null,
        registration_status: null,
      },
      requestContext
    );

    return {attendance: EventAttendance.parse(attendance)};
  });

/**
 * Removes the authenticated user's attendance from an event.
 */
export const removeAttending = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof MarkAttendingInput> => {
    const result = MarkAttendingInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    // Delete reminder schedule before removing attendance
    deleteReminderSchedule({
      profileId: profile.id,
      eventId: data.eventId,
      type: 'PLANNED_TO_RIDE',
    }).catch((err) =>
      console.error('Failed to delete attendance reminder:', err),
    );

    await db.eventAttendance.deleteMany({
      where: {
        profileId: profile.id,
        eventId: data.eventId,
      },
    });

    return {success: true};
  });

/**
 * Checks whether the current user is attending a given event.
 * Returns isAttending: false if not authenticated.
 */
export const getAttendanceStatus = createServerFn({method: 'GET'})
  .middleware([optionalAuthMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) throw new Error('Invalid event ID format');
    return result.data;
  })
  .handler(async ({data: eventId, context}) => {
    const auth = context.auth;
    if (!auth) return {isAttending: false as const};

    const db = await prismaClient();
    const attendance = await db.eventAttendance.findUnique({
      where: {
        profileId_eventId: {
          profileId: auth.profile.id,
          eventId,
        },
      },
    });

    return {isAttending: !!attendance};
  });

/**
 * Returns events from the current month onward that the authenticated user is attending,
 * ordered by startDate ascending. Returns [] when unauthenticated.
 */
export const getProfileAttendingEvents = createServerFn({
  method: 'GET',
})
  .middleware([optionalAuthMiddleware])
  .handler(async ({context}) => {
    const auth = context.auth;
    if (!auth) return [];

    const db = await prismaClient();
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const attendances = await db.eventAttendance.findMany({
      where: {
        profileId: auth.profile.id,
        event: {archived: false},
        OR: [
          {event: {endDate: {gte: startOfMonth}}},
          {event: {endDate: null, startDate: {gte: startOfMonth}}},
        ],
      },
      orderBy: {event: {startDate: 'asc'}},
      select: {
        event: {
          select: {
            id: true,
            name: true,
            startDate: true,
            endDate: true,
            location: true,
            heroUrl: true,
            eventType: true,
            discipline: true,
            externalId: true,
            lat: true,
            lng: true,
          },
        },
      },
    });

    return attendances.map((a) => a.event);
  });
